<script setup>
import { ref, onMounted } from 'vue';
import dayjs from 'dayjs'
import EmptyCard from '@/components/emptyCard.vue';
import { retrieve_children, retrieve_child_meta, show_trait_count } from '@/services/child';
import { get_report } from '@/services/report';
import { get_package } from '@/services/packages';
import loadingCard from '@/components/loading.vue';
import subscribedPill from './SubscribedPill.vue'
import { getAge, swalError } from '@/helper';
import subscriptionTime from './SubscriptionTime.vue'
import ChartCard from './ChartCard.vue';

const props = defineProps({
  traits:{
    default: Array
  }
})

const isLoading = ref(true)
const children = ref([])
onMounted(async ()=>{
  let response = await retrieve_children()
  if (response.error){
    swalError(response.result.error, true)
    isLoading.value = false
    return
  }

  if (response.result.data.status.empty){
    isLoading.value = false
    return
  }

  children.value = response.result.data.children.data
  children.value = await Promise.all(children.value.map(async(child) => {
    const needEmptyCard = {total: child.relationships.reports.data.length, hit: 0}
    const reports = await Promise.all(child.relationships.reports.data.map( async (report) => {
      let report_response = await get_report(report.id)
      
      // Handle error response - skip this report if API fails
      if (report_response.error || !report_response.result?.data?.included) {
        needEmptyCard.hit++
        return {
          report: null,
          intervention_traits: [],
        }
      }
      
      let intervention_traits = report_response.result.data.included.map( report_stats =>{
        if (report_stats.attributes.is_intervention){
          return report_stats
        }else{
          return null
        }
      })
      
      // get package config if included is exist
      if (report_response.result.data.data.attributes.document){
        let package_config_response = await get_package()
        package_config_response = package_config_response.result.find(item => item.id == report_response.result.data.data.attributes.report_type)
      }

      // remove null value
      intervention_traits = intervention_traits.filter(item => {
        return item !== null
      })

      if (intervention_traits.length == 0){
        needEmptyCard.hit++
      }else{
        intervention_traits = intervention_traits.sort((a,b) =>  new Date(a.updated_at) - new Date(b.updated_at))
      }
      return {
        report: report_response.result.data.data,
        intervention_traits,
      }
    }))

    let trait_left = await calculate_trait_left(child.id, reports)
    return {
      ...child,
      reports,
      needEmptyCard: needEmptyCard.hit == needEmptyCard.total ? true : false,
      trait_left
    }
  }))
  isLoading.value = false
})

async function calculate_trait_left(child_id, reports){
  let totalSelectedTrait = reports.map( item => item.intervention_traits.length).reduce((acc, curr) => acc + curr, 0)
  let available_trait = await get_extra_trait(child_id)
  let traitLeft = available_trait - totalSelectedTrait
  return traitLeft
}

async function get_trait_count(child_id){
  let response = await show_trait_count(child_id)
  if (response.error){
    swalError(response.result, true)
    return
  }
  return response.result.data
}

async function get_extra_trait(child_id){
  let subscribed_trait = await get_trait_count(child_id)
  let response = await retrieve_child_meta(child_id)
  if(response.error){
    swalError(response.result, true)
    return
  }
  let child_meta = response.result.data.data
  let extra_trait = child_meta.find(item => item.attributes.key == "extra_trait")
  subscribed_trait += (parseInt(extra_trait?.attributes?.value) || 0)
  return subscribed_trait
}

</script>

<template>
  <div class="col-md-12">
    <loadingCard v-if="isLoading"/>
    <template v-else>
      <div v-for="child in children" :key="child.id" class="card">
        <!-- Card Header -->
        <div class="card-header dashboard-child-header pb-0">
          <div class="child-header-bg">
            <img v-if="child.attributes.gender == 'female'" src="@/assets/images/girl.png" alt="" srcset="">
            <img v-else src="@/assets/images/boy.png" alt="" srcset="">
          </div>
          <div class="row d-flex justify-content-between align-items-top">
            
            <div class="col-md-6 child-info">
              <div class="d-flex align-items-center">
                <h5 class="me-2">{{ child.attributes.name }}</h5>
                <router-link class="btn btn-light-primary"
                  :to="{
                    name: 'dna-report-single-application', 
                    params: {id: child.attributes.id },
                    query: {report_only: true}
                  }"
                >
                  View Profile
                </router-link>
              </div>
              <subscribedPill :child="child" />
            </div>
            <div class="child-row-action col-md-6 row d-flex align-items-top">
              <div class="col-md-8">
                <div class="information me-3 chart-card-info d-flex">
                  <p class="m-auto">
                    <span class="text-secondary">Age : </span>
                    {{ getAge(child.attributes.date_of_birth) }}
                  </p>
                  <p class="m-auto">
                    <span class="text-secondary">Registered on : </span>
                    {{ dayjs(child.attributes.created_at).format('DD MMM YY') }}
                  </p>
                  
                </div>
                <subscriptionTime :child_id="child.id" />
              </div>
              <div class="col-md-4">
                <router-link
                  :disabled="child.trait_left <= 0" 
                  class="btn"
                  :to="{name: 'dna-report-single-application', params: {id: child.id }}"
                  :class="child.trait_left <= 0 ? 'disabled' : 'btn-light-success'"
                >
                  Select Trait
                </router-link>
              </div>
            </div>
            
          </div>
        </div>
  
        <div class="card-body row">
          <template v-if="!child.needEmptyCard" v-for="report in child.reports">
            <div v-for="trait in report.intervention_traits" class="col-md-4">
              <ChartCard :trait="trait" :child="child"/>
            </div>
          </template>
  
          <empty-card v-else :link="{
              routerObj: {
                name: 'dna-report-single-application',
                params: {id: child.id}
              },
              class: 'w-25',
              type: 'link',
              desc: 'Select Trait'
            }" 
          />
        </div>
      </div>
    </template>
    <div class="card" v-if="children.length == 0">
      <div class="card-body">
        <EmptyCard
          :link="{
            routerObj:{
              name: 'dna-report-submit'
            },
            type: 'link',
            class: 'w-25',
            desc: 'Register Your Child',
            text: 'No child has been registered yet'
          }"
        />  
      </div>
    </div>
  </div>
</template>
  